import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Divider,
  alpha,
  useTheme as useMuiTheme,
  ThemeProvider,
  createTheme,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AddCircle as AddCircleIcon,
  Group as GroupIcon,
  StackedLineChart as ChartIcon,
  Settings as SettingsIcon,
  DisplaySettings as DisplaySettingsIcon,
  Storefront as StorefrontIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import useSidebar from '../hooks/useSidebar';
import { hasRoutePermission } from '../utils/permissions';
import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';

// Componente para os cartões de atalho
const ShortcutCard = ({ icon, title, subtitle, path, color }) => {
  const navigate = useNavigate();
  const theme = useMuiTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: 3,
        transition: 'all 0.3s ease',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.shadows[6],
          borderColor: alpha(color, 0.3)
        },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '5px',
          height: '100%',
          backgroundColor: color,
          transition: 'all 0.3s ease'
        }
      }}
    >
      <CardActionArea 
        onClick={() => navigate(path)}
        sx={{ 
          height: '100%',
          p: 1
        }}
      >
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <Box sx={{ 
            mb: 2,
            p: 2,
            borderRadius: '50%',
            backgroundColor: alpha(color, 0.1),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {React.cloneElement(icon, { sx: { fontSize: 40, color: color } })}
          </Box>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// Componente para estatísticas
const StatCard = ({ title, value, icon, color }) => {
  const theme = useMuiTheme();
  
  return (
    <Card sx={{ 
      borderRadius: 3,
      height: '100%',
      boxShadow: theme.shadows[2],
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '30%',
        height: '5px',
        backgroundColor: color,
        borderRadius: '0 0 0 5px'
      }
    }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="subtitle1" color="textSecondary" fontWeight="500">
            {title}
          </Typography>
          <Box sx={{ 
            p: 1,
            borderRadius: '50%',
            backgroundColor: alpha(color, 0.1),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {React.cloneElement(icon, { sx: { fontSize: 24, color: color } })}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight="700" mt={2} sx={{ color }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

const Home = () => {
  const baseTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const { checkRoutePermission } = usePermissions();
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const [estatisticas, setEstatisticas] = useState({
    senhasHoje: 0,
    mediaTempoAtendimento: '0 min',
    senhasAguardando: 0,
    senhasAtendidas: 0
  });
  const [carregando, setCarregando] = useState(true);
  
  // Combinando temas
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      ...baseTheme.typography,
      fontFamily: 'Montserrat, sans-serif',
    },
  });
  
  // Formatando data de hoje
  const dataHoje = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const dataFormatada = dataHoje.charAt(0).toUpperCase() + dataHoje.slice(1);
  
  // Função para buscar estatísticas
  const buscarEstatisticas = async () => {
    try {
      setCarregando(true);
      const api = require('../services/api').default;
      const response = await api.get('/senhas/estatisticas');
      const data = response.data;
      
      if (data.success) {
        setEstatisticas({
          senhasHoje: data.data.senhasEmitidas || 0,
          mediaTempoAtendimento: data.data.tempoMedioAtendimento || '0 min',
          senhasAguardando: data.data.senhasAguardando || 0,
          senhasAtendidas: data.data.senhasFinalizadas || 0
        });
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    } finally {
      setCarregando(false);
    }
  };
  
  // Buscar estatísticas ao montar a página
  useEffect(() => {
    buscarEstatisticas();
    
    // Atualizar estatísticas a cada 30 segundos
    const intervalo = setInterval(buscarEstatisticas, 30000);
    
    return () => clearInterval(intervalo);
  }, []);
  
  // Verificar se o usuário tem permissão para acessar uma rota
  const temPermissao = (path) => {
    if (!user || !user.cargo) return false;
    return checkRoutePermission(path);
  };
  
  // Todos os atalhos do sistema
  const todosAtalhos = [
    {
      title: 'Chamar Senhas',
      subtitle: 'Acesse seu painel de atendimento',
      icon: <DashboardIcon />,
      path: '/dashboard',
      color: customTheme.palette.primary.main
    },
    {
      title: 'Emitir Senha',
      subtitle: 'Gere senhas para atendimento',
      icon: <AddCircleIcon />,
      path: '/emitir-senha',
      color: customTheme.palette.success.main
    },
    {
      title: 'Painel de Chamada',
      subtitle: 'Visualize senhas em atendimento',
      icon: <DisplaySettingsIcon />,
      path: '/painel-chamada',
      color: customTheme.palette.info.main
    },
    {
      title: 'Recepção',
      subtitle: 'Gerencie a fila de senhas',
      icon: <StorefrontIcon />,
      path: '/painel-recepcao',
      color: customTheme.palette.warning.main
    },
    {
      title: 'Relatórios',
      subtitle: 'Visualize estatísticas e análises',
      icon: <AssessmentIcon />,
      path: '/relatorios',
      color: '#ff5722' // Laranja
    },
    {
      title: 'Usuários',
      subtitle: 'Gerencie os usuários do sistema',
      icon: <GroupIcon />,
      path: '/usuarios',
      color: customTheme.palette.secondary.main
    },
    {
      title: 'Configurações',
      subtitle: 'Configure o sistema',
      icon: <SettingsIcon />,
      path: '/configuracoes',
      color: '#9c27b0' // Roxo
    }
  ];
  
  // Filtra os atalhos que o usuário tem permissão para acessar
  const atalhosFiltrados = todosAtalhos.filter(atalho => temPermissao(atalho.path));

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar sidebarOpen={sidebarOpen} customTheme={customTheme} />
        
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Header 
            title="Início" 
            sidebarOpen={sidebarOpen}
            handleSidebarToggle={toggleSidebar}
            customTheme={customTheme}
          />
          
          <Container maxWidth="xxl" sx={{ mt: 10, mb: 4 }}>
            {/* Cabeçalho com saudação */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 4, 
                mb: 4, 
                borderRadius: 3,
                background: `linear-gradient(45deg, ${alpha(customTheme.palette.primary.main, 0.8)}, ${alpha(customTheme.palette.secondary.main, 0.8)})`,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-50%',
                  right: '-10%',
                  width: '60%',
                  height: '200%',
                  background: `radial-gradient(circle, ${alpha('#fff', 0.1)} 0%, transparent 70%)`,
                  transform: 'rotate(-10deg)'
                }
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Olá, {user?.nome || 'Usuário'}!
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Bem-vindo ao Sistema de Gerenciamento de Senhas. Hoje é {dataFormatada}.
                </Typography>
              </Box>
            </Paper>
            
            {/* Estatísticas */}
            <Typography variant="h5" fontWeight="bold" mb={3}>
              Estatísticas de Hoje
            </Typography>
            
            <Grid container spacing={3} mb={6}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard 
                  title="Senhas Emitidas"
                  value={estatisticas.senhasHoje}
                  icon={<AddCircleIcon />}
                  color={customTheme.palette.primary.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard 
                  title="Tempo Médio"
                  value={estatisticas.mediaTempoAtendimento}
                  icon={<CalendarIcon />}
                  color={customTheme.palette.success.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard 
                  title="Em Espera"
                  value={estatisticas.senhasAguardando}
                  icon={<ChartIcon />}
                  color={customTheme.palette.warning.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard 
                  title="Atendidas"
                  value={estatisticas.senhasAtendidas}
                  icon={<GroupIcon />}
                  color={customTheme.palette.info.main}
                />
              </Grid>
            </Grid>
            
            {/* Atalhos */}
            <Typography variant="h5" fontWeight="bold" mb={3}>
              Acesso Rápido
            </Typography>
            
            <Grid container spacing={3}>
              {atalhosFiltrados.map((atalho, index) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                  <ShortcutCard {...atalho} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Home; 